import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../../api/api';
import { getAllContentsDetails } from '../../../../api/contents';
import '../../../../assets/styles/contents-list.scss';

interface ContentItem {
    id: number;
    title: string;
    type: string;
    language: string;
    is_visible: boolean;
    has_description: boolean;
    is_premium: boolean;
    created_at: string;
    audio_titles: string[];
    authors?: string;
    tags?: string[];
    created_by_name?: string | null;
    created_by_email?: string | null;
    last_edited_by_name?: string | null;
    last_edited_by_email?: string | null;
    last_edited_at?: string | null;
    avg_rating?: number | null;
    ratings_count?: number | null;
    /** PRZYCHODZI Z BACKENDU dzięki JOIN categories */
    category_id?: number | null;
    category_title?: string | null;
}

// UWAGA: brak 'id', 'title' i 'type' — zawsze widoczne
type ColumnKey =
    | 'category'
    | 'authors'
    | 'tags'
    | 'avg_rating'
    | 'is_visible'
    | 'has_description'
    | 'audio'
    | 'created_at'
    | 'is_premium'
    | 'created_by'
    | 'last_edit'
    | 'actions';

/* ===== Etykiety i domyślna widoczność kolumn (jak w Users) ===== */
const COLUMN_LABELS: Record<ColumnKey, string> = {
    category: 'Kategoria',
    authors: 'Autorzy',
    tags: 'Tagi',
    avg_rating: 'Śr. ocen',
    is_visible: 'Widoczność',
    has_description: 'Opis',
    audio: 'Audio',
    created_at: 'Data',
    is_premium: 'Premium',
    created_by: 'Utworzył',
    last_edit: 'Ostatnia edycja',
    actions: 'Akcje',
};

const DEFAULT_VISIBLE_COLS: Record<ColumnKey, boolean> = {
    category: true,
    authors: false,
    tags: false,
    avg_rating: true,
    is_visible: true,
    has_description: false,
    audio: false,
    created_at: true,
    is_premium: true,
    created_by: false,
    last_edit: false,
    actions: true,
};

export default function ContentsList() {
    const didRun = React.useRef(false);
    const [contents, setContents] = useState<ContentItem[]>([]);
    const [filtered, setFiltered] = useState<ContentItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
    const [showDateFilters, setShowDateFilters] = useState<boolean>(false);
    const navigate = useNavigate();

    // filtry
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [visibilityFilter, setVisibilityFilter] = useState('');
    const [descriptionFilter, setDescriptionFilter] = useState(false);
    const [audioFilter, setAudioFilter] = useState('');
    const [premiumFilter, setPremiumFilter] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | 'ALL'>('ALL');

    // kategorie (z danych — nie fetchujemy oddzielnie)
    const categoryOptions = useMemo(() => {
        const set = new Set<string>();
        contents.forEach(c => {
            if (c.category_title && c.category_title.trim()) set.add(c.category_title.trim());
            else set.add('—'); // brak kategorii
        });
        // posortuj alfabetycznie, „—” na koniec
        const arr = Array.from(set);
        arr.sort((a, b) => {
            if (a === '—') return 1;
            if (b === '—') return -1;
            return a.localeCompare(b);
        });
        return arr;
    }, [contents]);

    /* ===== Widoczność kolumn (z localStorage) + panel ===== */
    const [visibleCols, setVisibleCols] = useState<Record<ColumnKey, boolean>>(() => {
        try {
            const saved = localStorage.getItem('contents_visibleCols');
            return saved ? { ...DEFAULT_VISIBLE_COLS, ...JSON.parse(saved) } : DEFAULT_VISIBLE_COLS;
        } catch {
            return DEFAULT_VISIBLE_COLS;
        }
    });
    useEffect(() => {
        localStorage.setItem('contents_visibleCols', JSON.stringify(visibleCols));
    }, [visibleCols]);

    const [colsOpen, setColsOpen] = useState(false);
    const colsBtnRef = useRef<HTMLButtonElement | null>(null);
    const colsPanelRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!colsOpen) return;
        function onDocMouseDown(e: MouseEvent) {
            const t = e.target as Node;
            if (colsPanelRef.current?.contains(t) || colsBtnRef.current?.contains(t)) return;
            setColsOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setColsOpen(false);
        }
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [colsOpen]);

    const [confirmId, setConfirmId] = useState<number | null>(null);
    const [confirmTitle, setConfirmTitle] = useState<string>('');
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    const fetchContents = async () => {
        setLoading(true);
        try {
            const data = await getAllContentsDetails();
            if (Array.isArray(data)) {
                setContents(data);
                setFiltered(data);
            } else {
                setContents([]);
                setFiltered([]);
            }
        } catch {
            setContents([]);
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (didRun.current) return;
        didRun.current = true;
        fetchContents();
    }, []);

    useEffect(() => {
        const lowerSearch = search.toLowerCase();
        const fromDate = dateFrom ? new Date(dateFrom) : null;
        const toDate = dateTo ? new Date(dateTo) : null;

        const next = contents
            .filter(c => {
                const createdAt = new Date(c.created_at);
                const catTitle = (c.category_title && c.category_title.trim()) ? c.category_title.trim() : '—';
                return (
                    (c.title.toLowerCase().includes(lowerSearch) || String(c.id).includes(lowerSearch)) &&
                    (typeFilter ? c.type === typeFilter : true) &&
                    (languageFilter ? c.language === languageFilter : true) &&
                    (visibilityFilter ? c.is_visible === (visibilityFilter === 'true') : true) &&
                    (!descriptionFilter || c.has_description) &&
                    (audioFilter ? (audioFilter === 'true' ? (c.audio_titles?.length ?? 0) > 0 : (c.audio_titles?.length ?? 0) === 0) : true) &&
                    (!premiumFilter || c.is_premium) &&
                    (!fromDate || createdAt >= fromDate) &&
                    (!toDate || createdAt <= toDate) &&
                    (categoryFilter === 'ALL' ? true : catTitle === categoryFilter)
                );
            })
            .sort((a, b) => a.id - b.id);

        setFiltered(next);
    }, [search, contents, typeFilter, languageFilter, visibilityFilter, descriptionFilter, audioFilter, dateFrom, dateTo, premiumFilter, categoryFilter]);

    const handleAddNew = () => navigate('/admin/contents/new');
    const handleEdit = (id: number) => navigate(`/admin/contents/edit/${id}`);

    const openConfirm = (item: ContentItem) => {
        setDeleteError(null);
        setConfirmId(item.id);
        setConfirmTitle(item.title);
    };
    const closeConfirm = () => {
        setConfirmId(null);
        setConfirmTitle('');
        setDeleteError(null);
        setDeleting(false);
    };

    const handleDelete = async () => {
        if (!confirmId) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await api.delete(`/contents/${confirmId}`);
            setContents(prev => prev.filter(c => c.id !== confirmId));
            setFiltered(prev => prev.filter(c => c.id !== confirmId));
            closeConfirm();
        } catch (e: any) {
            const serverMsg = e?.data?.error || e?.message || 'Nie udało się usunąć';
            const msg = (serverMsg || '').toLowerCase();
            if (msg.includes('owner') || msg.includes('admins only') || msg.includes('forbidden') || msg.includes('not allowed')) {
                setDeleteError('Nie masz uprawnień do usunięcia tego zasobu.');
            } else {
                setDeleteError(serverMsg);
            }
            setDeleting(false);
        }
    };

    function formatRating(avg?: number | null, count?: number | null) {
        if (!avg || !count) return '—';
        const v = Math.round((avg as number) * 10) / 10;
        return `${v} (${count})`;
    }

    return (
        <div className="contents-list-container">
            <div className="contents-header-bar">
                <h2>Lista treści</h2>

                <div className="action-buttons">
                    {/* Filtry */}
                    <button className="btn ghost" onClick={() => setFiltersVisible(v => !v)}>
                        <span className="ico" aria-hidden>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                            </svg>
                        </span>
                        <span className="label">Filtry</span>
                    </button>

                    <button className="btn primary" onClick={handleAddNew}>
                        <span className="ico" aria-hidden>
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                        </span>
                        <span className="label">Dodaj treść</span>
                    </button>
                </div>
            </div>

            {filtersVisible && (
                <div className="filters-container">
                    <div className="filters-row">
                        <input
                            type="text"
                            placeholder="Szukaj po ID lub tytule…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />

                        <button className="date-toggle-btn" onClick={() => setShowDateFilters(v => !v)}>
                            Data
                        </button>
                        {showDateFilters && (
                            <>
                                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                            </>
                        )}

                        <select className="filter-narrow" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                            <option value="">Typ</option>
                            {[...new Set(contents.map(c => c.type))].map(t => (<option key={t} value={t}>{t}</option>))}
                        </select>

                        <select value={languageFilter} onChange={e => setLanguageFilter(e.target.value)}>
                            <option value="">Język</option>
                            {[...new Set(contents.map(c => c.language))].map(l => (<option key={l} value={l}>{l}</option>))}
                        </select>

                        <select className="filter-narrow" value={visibilityFilter} onChange={e => setVisibilityFilter(e.target.value)}>
                            <option value="">Widoczność</option>
                            <option value="true">✅</option>
                            <option value="false">❌</option>
                        </select>

                        <select className="filter-narrow" value={audioFilter} onChange={e => setAudioFilter(e.target.value)}>
                            <option value="">Audio</option>
                            <option value="true">✅</option>
                            <option value="false">❌</option>
                        </select>

                        <label className="filter-checkbox">
                            <input type="checkbox" checked={descriptionFilter} onChange={e => setDescriptionFilter(e.target.checked)} />
                            Opis
                        </label>

                        <label className="filter-checkbox">
                            <input type="checkbox" checked={premiumFilter} onChange={e => setPremiumFilter(e.target.checked)} />
                            Premium
                        </label>

                        {/* Kategoria z danych backendu */}
                        <select
                            className="filter-narrow"
                            value={categoryFilter === 'ALL' ? '' : String(categoryFilter)}
                            onChange={(e) => setCategoryFilter(e.target.value ? e.target.value : 'ALL')}
                        >
                            <option value="">Kategoria</option>
                            {categoryOptions.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Widoczność kolumn — jak w Users */}
            <div className="cols-control">
                <button
                    type="button"
                    className="btn ghost cols-btn"
                    onClick={() => setColsOpen(v => !v)}
                    aria-expanded={colsOpen}
                    title="Widoczność kolumn"
                    ref={colsBtnRef}
                    style={{ marginLeft: 8 }}
                >
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    <span>Widoczność</span>
                </button>

                {colsOpen && (
                    <div
                        className="cols-panel"
                        role="dialog"
                        aria-label="Wybór kolumn"
                        ref={colsPanelRef}
                    >
                        {(Object.keys(COLUMN_LABELS) as ColumnKey[]).map((k) => (
                            <label key={k} className="filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={!!visibleCols[k]}
                                    onChange={(e) => setVisibleCols((v) => ({ ...v, [k]: e.target.checked }))}
                                />
                                {COLUMN_LABELS[k]}
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* Scroll TYLKO dla tabeli */}
            <div className="table-wrap">
                <table className="contents-table">
                    <thead>
                        <tr>
                            <th className="col-id">ID</th>
                            <th className="col-title">Tytuł</th>
                            <th className="col-type">Typ</th>

                            {visibleCols.category && <th className="col-category">Kategoria</th>}
                            {visibleCols.authors && <th className="col-authors">Autorzy</th>}
                            {visibleCols.tags && <th className="col-tags">Tagi</th>}
                            {visibleCols.avg_rating && <th className="col-rating">Śr. ocen</th>}
                            {visibleCols.is_visible && <th>Widoczność</th>}
                            {visibleCols.has_description && <th>Opis</th>}
                            {visibleCols.audio && <th className="col-audio">Audio</th>}
                            {visibleCols.created_at && <th>Data</th>}
                            {visibleCols.is_premium && <th>Premium</th>}
                            {visibleCols.created_by && <th className="col-created-by">Utworzył</th>}
                            {visibleCols.last_edit && <th className="col-last-edit">Ostatnia edycja</th>}
                            {visibleCols.actions && <th></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={3 + Object.values(visibleCols).filter(Boolean).length}>Ładowanie...</td></tr>
                        ) : (
                            filtered.map(content => {
                                const tagsJoined = Array.isArray(content.tags) && content.tags.length ? content.tags.join(', ') : '—';
                                const audioShort =
                                    content.audio_titles?.length
                                        ? content.audio_titles.map(t => t.replace(/\.[^/.]+$/, '')).slice(0, 2).join(', ')
                                        + (content.audio_titles.length > 2 ? '...' : '')
                                        : '—';
                                const createdBy = content.created_by_name || content.created_by_email || '—';
                                const lastEditUser = content.last_edited_by_name || content.last_edited_by_email || '—';
                                const lastEditDate = content.last_edited_at ? new Date(content.last_edited_at).toLocaleString() : '—';
                                const categoryName = (content.category_title && content.category_title.trim()) ? content.category_title.trim() : '—';

                                return (
                                    <tr key={content.id}>
                                        <td className="col-id-val">{content.id}</td>

                                        <td className="col-title-val">
                                            <div className="cell-scroll" title={content.title || '—'}>{content.title || '—'}</div>
                                        </td>

                                        <td className="col-type-val">{content.type}</td>

                                        {visibleCols.category && (
                                            <td className="col-category-val">
                                                <div className="cell-scroll" title={categoryName}>{categoryName}</div>
                                            </td>
                                        )}

                                        {visibleCols.authors && (
                                            <td><div className="cell-scroll" title={content.authors || '—'}>{content.authors || '—'}</div></td>
                                        )}

                                        {visibleCols.tags && (
                                            <td><div className="cell-scroll" title={tagsJoined}>{tagsJoined}</div></td>
                                        )}

                                        {visibleCols.avg_rating && (
                                            <td className="strong" title={formatRating(content.avg_rating, content.ratings_count)}>
                                                {formatRating(content.avg_rating, content.ratings_count)}
                                            </td>
                                        )}

                                        {visibleCols.is_visible && (
                                            <td className="strong">
                                                <span className={`badge ${content.is_visible ? 'badge-success' : 'badge-muted'}`}>{content.is_visible ? 'TAK' : 'NIE'}</span>
                                            </td>
                                        )}

                                        {visibleCols.has_description && (
                                            <td className="strong">
                                                <span className={`badge ${content.has_description ? 'badge-success' : 'badge-muted'}`}>{content.has_description ? 'TAK' : 'NIE'}</span>
                                            </td>
                                        )}

                                        {visibleCols.audio && (
                                            <td><div className="cell-scroll" title={audioShort}>{audioShort}</div></td>
                                        )}

                                        {visibleCols.created_at && <td>{new Date(content.created_at).toLocaleDateString()}</td>}

                                        {visibleCols.is_premium && (
                                            <td className="strong">
                                                <span className={`badge ${content.is_premium ? 'badge-success' : 'badge-muted'}`}>{content.is_premium ? 'TAK' : 'NIE'}</span>
                                            </td>
                                        )}

                                        {visibleCols.created_by && (
                                            <td><div className="cell-scroll" title={createdBy}>{createdBy}</div></td>
                                        )}

                                        {visibleCols.last_edit && (
                                            <td>
                                                <div className="cell-scroll" title={`${lastEditUser} • ${lastEditDate}`}>
                                                    {lastEditUser} • {lastEditDate}
                                                </div>
                                            </td>
                                        )}

                                        {visibleCols.actions && (
                                            <td className="actions-cell">
                                                <button className="icon-btn edit" onClick={() => handleEdit(content.id)} aria-label="Edytuj">
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M12 20h9"></path>
                                                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                                                    </svg>
                                                </button>
                                                <button className="icon-btn delete" onClick={() => openConfirm(content)} aria-label="Usuń">
                                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M3 6h18"></path>
                                                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                                    </svg>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {confirmId !== null && (
                <div className="modal-overlay" onClick={closeConfirm}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>Czy na pewno usunąć?</h3>
                        <p className="modal-desc">„{confirmTitle}” zostanie trwale usunięta.</p>
                        {deleteError && <div className="modal-error">{deleteError}</div>}
                        <div className="modal-actions">
                            <button onClick={handleDelete} disabled={deleting}>{deleting ? 'Usuwanie…' : 'Tak, usuń'}</button>
                            <button className="secondary" onClick={closeConfirm}>Anuluj</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
